import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { push } from 'redux-first-history';
import { snakeToCamel } from '@rossum/api-client/utils';
import {
  IconCalendar,
  IconCircleDot,
  IconCirclePlus,
  IconFolder,
  IconFolders,
  IconHistory,
  IconSTurnRight,
  IconTag,
} from '@rossum/ui/icons/tabler';
import {
  Button,
  Collapse,
  Popover,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { DocumentStatus } from '../../../../../components/UI/DocumentStatus';
import { isEmbedded } from '../../../../../constants/config';
import { refetchAnnotation } from '../../../../../redux/modules/annotation/actions';
import { annotationSideloadsSelector } from '../../../../../redux/modules/annotation/selectors';
import { pauseValidation } from '../../../../../redux/modules/beforeLeave/actions';
import { documentHistoryEnabledSelector } from '../../../../../redux/modules/organizationGroup/selectors';
import { isUserAtLeastManager } from '../../../../../redux/modules/user/selectors';
import { Annotation } from '../../../../../types/annotation';
import { useSetTrainingEnabled } from '../../../../document-list/selection-panel/hooks/useSetTrainingEnabled';
import {
  SidebarItemLabel,
  SidebarItemLayout,
  SidebarSectionTitle,
  VerticalContainer,
} from '../../../../document-sidebar';
import {
  AnnotationsLabelsList,
  OnLabelAction,
} from '../../../../labels/AnnotationsLabelsList';
import { useRequestLabelsApply } from '../../../../labels/hooks/useRequestLabelsApply';
import { labelsFeatureSelector } from '../../../../pricing/selectors';
import { notify } from '../../../../toaster';
import { useDocumentStore } from '../../../document-store/DocumentStore';
import { Messages } from '../../sidebar-items/shared/Messages';
import { groupMessagesByType } from '../automation-blockers/helpers';
import { DateDetail } from './DateDetail';
import { LabelsList } from './LabelsList';
import { TrainingToggle } from './TrainingToggle';
import { useShowWorkflowActivity } from './useShowWorkflowActivity';

type AnnotationDataProps = {
  annotation: Annotation | undefined;
};

const AnnotationData = React.memo(({ annotation }: AnnotationDataProps) => {
  const intl = useIntl();

  const annotationDataVisible = useDocumentStore(
    state => state.sidebarState.annotationDataVisible
  );

  const containerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (containerRef.current && annotationDataVisible) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [annotationDataVisible]);

  // TODO: Why do we keep needing to use selectors and why are sideloads spearated in types when they are in redux :thinking:
  const annotationSideloads = useSelector(annotationSideloadsSelector);

  const modifier = annotationSideloads.modifier?.user?.email ?? '-';

  // for compatibility with a component displaying the chip
  const documentStatusModifier = annotationSideloads.modifier?.user
    ? {
        id: annotationSideloads.modifier.user.id,
        username: annotationSideloads.modifier.user.email ?? '-',
      }
    : undefined;

  // Confirmed/exported by
  const { confirmedBy, exportedBy } = annotationSideloads;

  // Workflows
  const showWorkflowActivity = useShowWorkflowActivity(annotation?.id);

  const openDrawer = useDocumentStore(state => state.openDrawer);

  const documentHistoryEnabled = useSelector(documentHistoryEnabledSelector);

  // Training toggle
  const isAtLeastManager = useSelector(isUserAtLeastManager);
  const isDeletedOrPurged =
    annotation?.status === 'deleted' || annotation?.status === 'purged';
  const showTrainingToggle = isAtLeastManager;
  const { setTrainingEnabled, isLoading: isTrainingLoading } =
    useSetTrainingEnabled();

  // Labels
  const labelsEnabled = useSelector(labelsFeatureSelector);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const openLabelsPopover = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const closeLabelsPopover = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const { mutate: onApplyLabel } = useRequestLabelsApply();

  const dispatch = useDispatch();

  const handleLabelsChanged: OnLabelAction = useCallback(
    ({ payload }) => {
      onApplyLabel(payload, {
        onSuccess: () => {
          if (annotation) {
            dispatch(refetchAnnotation(annotation.url));
          }

          notify.default({
            title: intl.formatMessage({
              id: 'components.sidebarV2.annotationData.labelsList.apply.success',
            }),
          });

          closeLabelsPopover();
        },
        onError: () => {
          notify.error({
            title: intl.formatMessage({
              id: 'components.sidebarV2.annotationData.labelsList.apply.error',
            }),
          });
        },
      });
    },
    [annotation, closeLabelsPopover, dispatch, intl, onApplyLabel]
  );

  const handleLabelDelete = useCallback(
    (labelUrl: string) => {
      if (annotation) {
        handleLabelsChanged({
          payload: {
            operations: {
              remove: [labelUrl],
            },
            objects: {
              annotations: [annotation?.url],
            },
          },
        });
      }
    },
    [annotation, handleLabelsChanged]
  );

  const annotationMessages = useMemo(() => {
    const { errorMessages, warningMessages, infoMessages } =
      groupMessagesByType(annotation?.messages);
    return [...errorMessages, ...warningMessages, ...infoMessages];
  }, [annotation?.messages]);

  const queueLink = annotationSideloads.queue?.id
    ? `/queues/${annotationSideloads.queue.id}`
    : undefined;

  const handleQueueLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (e.metaKey || e.ctrlKey || !queueLink) return;

      e.preventDefault();
      e.stopPropagation();

      dispatch(
        pauseValidation({
          nextAction: push(queueLink),
          trigger: 'cancelAnnotation',
          reason: 'surveys',
        })
      );
    },
    [dispatch, queueLink]
  );

  return (
    <Collapse in={annotationDataVisible} ref={containerRef}>
      <SidebarSectionTitle
        title={intl.formatMessage({
          id: 'components.sidebarV2.annotationData.title',
        })}
      />
      <Stack sx={{ pl: 2, pr: 1 }} color="text.secondary">
        {!isEmbedded() ? (
          <>
            <SidebarItemLayout
              iconSlot={
                <SvgIcon
                  fontSize="medium"
                  sx={{
                    display: 'block',
                  }}
                >
                  <IconFolders />
                </SvgIcon>
              }
              labelSlot={
                <SidebarItemLabel
                  label={intl.formatMessage({
                    id: 'components.sidebarV2.annotationData.labels.workspace',
                  })}
                />
              }
              valueSlot={
                <VerticalContainer>
                  <Typography variant="body2" color="text.secondary">
                    {annotationSideloads.workspace?.name}
                  </Typography>
                </VerticalContainer>
              }
            />
            <SidebarItemLayout
              iconSlot={
                <SvgIcon
                  fontSize="medium"
                  sx={{
                    display: 'block',
                  }}
                >
                  <IconFolder />
                </SvgIcon>
              }
              labelSlot={
                <SidebarItemLabel
                  label={intl.formatMessage({
                    id: 'components.sidebarV2.annotationData.labels.queue',
                  })}
                />
              }
              valueSlot={
                <VerticalContainer>
                  {queueLink && (
                    <Typography
                      variant="body2"
                      component={RouterLink}
                      color="text.secondary"
                      sx={{
                        '&:hover': { color: 'inherit' },
                        alignSelf: 'flex-start',
                      }}
                      to={queueLink}
                      onClick={handleQueueLinkClick}
                    >
                      {annotationSideloads.queue?.name}
                    </Typography>
                  )}
                </VerticalContainer>
              }
            />
          </>
        ) : null}
        {annotation && annotationSideloads ? (
          <SidebarItemLayout
            iconSlot={
              <SvgIcon
                fontSize="medium"
                sx={{
                  display: 'block',
                }}
              >
                <IconCircleDot />
              </SvgIcon>
            }
            labelSlot={
              <SidebarItemLabel
                label={intl.formatMessage({
                  id: 'components.sidebarV2.annotationData.labels.status',
                })}
              />
            }
            valueSlot={
              <VerticalContainer>
                <span>
                  <DocumentStatus
                    status={annotation.status}
                    modifier={documentStatusModifier}
                    disabled={annotation.restrictedAccess}
                    dataCy={`status-name-${snakeToCamel(annotation.status)}`}
                  />
                </span>
              </VerticalContainer>
            }
          />
        ) : null}
        {annotation && showTrainingToggle ? (
          <TrainingToggle
            checked={
              isDeletedOrPurged ? false : (annotation.trainingEnabled ?? true)
            }
            disabled={annotation.restrictedAccess || isDeletedOrPurged}
            isLoading={isTrainingLoading}
            onChange={enabled =>
              setTrainingEnabled({
                annotationIds: [annotation.id],
                enabled,
                onSuccess: () => {
                  dispatch(refetchAnnotation(annotation.url));
                },
                onError: () => {
                  notify.error({
                    title: intl.formatMessage({
                      id: 'components.sidebarV2.annotationData.trainingToggle.error',
                    }),
                  });
                },
              })
            }
          />
        ) : null}
        <SidebarItemLayout
          iconSlot={
            <SvgIcon
              fontSize="medium"
              sx={{
                display: 'block',
              }}
            >
              <IconCalendar />
            </SvgIcon>
          }
          labelSlot={
            <SidebarItemLabel
              label={intl.formatMessage({
                id: 'components.sidebarV2.annotationData.labels.created',
              })}
            />
          }
          valueSlot={
            <VerticalContainer>
              <DateDetail dateString={annotation?.createdAt} />
            </VerticalContainer>
          }
        />
        <SidebarItemLayout
          iconSlot={
            <SvgIcon
              fontSize="medium"
              sx={{
                display: 'block',
              }}
            >
              <IconCalendar />
            </SvgIcon>
          }
          labelSlot={
            <SidebarItemLabel
              label={intl.formatMessage({
                id: 'components.sidebarV2.annotationData.labels.edited',
              })}
            />
          }
          valueSlot={
            <VerticalContainer>
              <DateDetail dateString={annotation?.modifiedAt} />
              <Typography variant="caption" color="text.secondary">
                {modifier}
              </Typography>
            </VerticalContainer>
          }
        />
        {annotation?.confirmedAt ? (
          <SidebarItemLayout
            iconSlot={
              <SvgIcon
                fontSize="medium"
                sx={{
                  display: 'block',
                }}
              >
                <IconCalendar />
              </SvgIcon>
            }
            labelSlot={
              <SidebarItemLabel
                label={intl.formatMessage({
                  id: 'components.sidebarV2.annotationData.labels.confirmed',
                })}
              />
            }
            valueSlot={
              <VerticalContainer>
                <DateDetail dateString={annotation?.confirmedAt} />
                <Typography variant="caption" color="text.secondary">
                  {confirmedBy?.email ?? '-'}
                </Typography>
              </VerticalContainer>
            }
          />
        ) : null}
        {annotation?.exportedAt ? (
          <SidebarItemLayout
            iconSlot={
              <SvgIcon
                fontSize="medium"
                sx={{
                  display: 'block',
                }}
              >
                <IconCalendar />
              </SvgIcon>
            }
            labelSlot={
              <SidebarItemLabel
                label={intl.formatMessage({
                  id: 'components.sidebarV2.annotationData.labels.exported',
                })}
              />
            }
            valueSlot={
              <VerticalContainer>
                <DateDetail dateString={annotation?.exportedAt} />
                <Typography variant="caption" color="text.secondary">
                  {exportedBy?.email ?? '-'}
                </Typography>
              </VerticalContainer>
            }
          />
        ) : null}
        {showWorkflowActivity ? (
          <SidebarItemLayout
            iconSlot={
              <SvgIcon fontSize="medium" sx={{ display: 'block' }}>
                <IconSTurnRight />
              </SvgIcon>
            }
            labelSlot={
              <SidebarItemLabel
                label={intl.formatMessage({
                  id: 'components.sidebarV2.annotationData.labels.workflows',
                })}
              />
            }
            valueSlot={
              <VerticalContainer>
                <Button
                  variant="text"
                  size="small"
                  color="secondary"
                  sx={{
                    alignSelf: 'flex-start',
                    // TODO: @ui put this into theme?
                    minWidth: 'unset',
                    px: 0.5,
                    py: 0.5,
                  }}
                  onClick={
                    annotation
                      ? () =>
                          openDrawer({
                            drawer: 'activities',
                            annotationId: annotation.id,
                          })
                      : undefined
                  }
                >
                  {intl.formatMessage({
                    id: 'components.sidebarV2.annotationData.buttons.open',
                  })}
                </Button>
              </VerticalContainer>
            }
          />
        ) : null}
        {documentHistoryEnabled && annotation ? (
          <SidebarItemLayout
            iconSlot={
              <SvgIcon fontSize="medium" sx={{ display: 'block' }}>
                <IconHistory />
              </SvgIcon>
            }
            labelSlot={
              <SidebarItemLabel
                label={intl.formatMessage({
                  id: 'features.documentHistory.openButton',
                })}
              />
            }
            valueSlot={
              <VerticalContainer>
                <Button
                  data-cy="document-history-button"
                  variant="text"
                  size="small"
                  color="secondary"
                  sx={{
                    alignSelf: 'flex-start',
                    minWidth: 'unset',
                    px: 0.5,
                    py: 0.5,
                  }}
                  onClick={() =>
                    openDrawer({
                      drawer: 'documentHistory',
                      annotationId: annotation.id,
                    })
                  }
                >
                  {intl.formatMessage({
                    id: 'features.documentHistory.openButton',
                  })}
                </Button>
              </VerticalContainer>
            }
          />
        ) : null}
        {labelsEnabled ? (
          <SidebarItemLayout
            iconSlot={
              <SvgIcon
                fontSize="medium"
                sx={{
                  display: 'block',
                }}
              >
                <IconTag />
              </SvgIcon>
            }
            labelSlot={
              <SidebarItemLabel
                label={intl.formatMessage({
                  id: 'components.sidebarV2.annotationData.labels.labels',
                })}
              />
            }
            valueSlot={
              <VerticalContainer spacing={1}>
                <LabelsList
                  labels={annotationSideloads.labels}
                  onLabelDelete={handleLabelDelete}
                />
                <Button
                  variant="text"
                  size="small"
                  color="secondary"
                  startIcon={
                    <SvgIcon fontSize="medium">
                      <IconCirclePlus />
                    </SvgIcon>
                  }
                  sx={{
                    alignSelf: 'flex-start',
                  }}
                  onClick={openLabelsPopover}
                >
                  {intl.formatMessage({
                    id: 'components.sidebarV2.annotationData.labels.labels.addLabel',
                  })}
                </Button>
                <Popover
                  open={!!anchorEl}
                  onClose={closeLabelsPopover}
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    horizontal: 'left',
                    vertical: 'bottom',
                  }}
                  // PaperProps={{
                  //   sx: {
                  //     maxHeight: MENU_MAX_HEIGHT,
                  //     minHeight: MENU_MIN_HEIGHT,
                  //     width: MENU_WIDTH,
                  //   },
                  // }}
                  // TransitionProps={{
                  //   onExited: () => {
                  //     if (isExiting) setIsExiting(false);
                  //   },
                  // }}
                >
                  <AnnotationsLabelsList
                    annotations={annotation ? [annotation] : []}
                    onClose={closeLabelsPopover}
                    onLabelsChanged={handleLabelsChanged}
                  />
                </Popover>
              </VerticalContainer>
            }
          />
        ) : null}
      </Stack>
      {annotationMessages.length > 0 ? (
        <>
          <SidebarSectionTitle
            title={intl.formatMessage({
              id: 'components.sidebarV2.annotationData.messages.title',
            })}
          />
          <Stack sx={{ pl: 2, pr: 1, mt: '-5px' }}>
            <Typography variant="caption" color="text.secondary">
              {intl.formatMessage({
                id: 'components.sidebarV2.annotationData.messages.description',
              })}
            </Typography>
            <VerticalContainer>
              <Messages
                messagesVariant="annotation"
                messages={annotationMessages}
              />
            </VerticalContainer>
          </Stack>
        </>
      ) : null}
    </Collapse>
  );
});

AnnotationData.displayName = 'AnnotationData';

export { AnnotationData };
